import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import { Box, Chip, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  ProtectionLevelOrder,
  ProtectionLevelText,
} from "../../../../utils/constants";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { icons } from "../../../stack/environment/icons/iconExport";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import DetectionLabelIcon from "../../detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../detections/components/DetectionsTooltip";
import { getActiveDetections } from "../../detections/components/utils";
import CoverageControlsLabel from "./CoverageControlsLabel";
import ProtectionLevel from "./ProtectionLevel";

export const protectionToolTipTitle = (compliant, total) => {
  const isSingular = total === 1;
  const controlText = isSingular ? "control" : "controls";
  if (compliant === 0) {
    return `None of the controls are compliant`;
  }

  if (compliant === total) {
    return `All ${controlText} are compliant`;
  }

  const verb = compliant === 1 ? "is" : "are";
  return `${compliant} out of ${total} ${controlText} ${verb} compliant`;
};

export default function StepsTable({
  onRowClick,
  steps,
  loading,
  refToPass = undefined,
  onRowSelection,
  rowNumber = null,
  selectedRowIDs,
  filteredScenariosSets = null,
  sx = {},
}) {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const colors = useColors();
  const attackPathColumns = [
    {
      field: "number_of_scenarios",
      headerName: "Attack Paths",
      width: 140,
      sortable: true,
      sortComparator: (v1, v2) => v2 - v1,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent={"space-between"}
            gap={2}
          >
            <Typography variant="body2">{params.value}</Typography>

            <Typography
              variant="body2"
              sx={{
                color: colors.text_secondary,
              }}
            >
              {"(" + params.row.scenario_involvement_percentage + "%)"}
            </Typography>
          </Box>
        );
      },
    },
  ];
  const impactColumns = {
    headerAlign: "left",
    width: 140,
    sortable: true,
    align: "left",
    field: "impact",
    headerName: "Impact",
    renderHeader: (params) => (
      <Typography variant="overline">{params.colDef?.headerName}</Typography>
    ),
    valueFormatter: (params) => {
      return params.value + "%";
    },
    valueGetter: (params) => {
      const coverageImprovement = Math.round(
        (params.row.scenariosCount /
          filteredScenariosSets
            ?.map((scenarioSet) => scenarioSet.scenario_count)
            .reduce((a, b) => a + b, 0)) *
          100
      );
      return coverageImprovement === 0 ? 1 : coverageImprovement;
    },
    sortComparator: (v1, v2) => {
      return v2 - v1;
    },
    renderCell: (params) => {
      const coverageImprovement = params.value;
      return (
        <Box display={"flex"} gap={1} alignItems={"center"}>
          {filteredScenariosSets?.length > 0 && (
            <Box display={"flex"} gap={1} alignItems={"center"}>
              <Typography variant="caption">
                {coverageImprovement + "%"}
              </Typography>
              <NorthRoundedIcon
                sx={{
                  color: colors.success_400,
                  width: "16px",
                  height: "16px",
                }}
              />
            </Box>
          )}
        </Box>
      );
    },
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      renderCell: (params) => {
        return <Typography variant="body2">#{params.value}</Typography>;
      },
    },
    {
      field: "name",
      headerName: "Attack Step",
      flex: 0.4,
      renderCell: (params) => {
        const maxSeverity = params.row?.last_detections
          ? Math.max(
              ...params.row?.last_detections.map(
                (detection) => detection.severity
              )
            )
          : 0;
        return (
          <Box
            display="flex"
            alignItems={"center"}
            flexDirection={"row"}
            gap={1}
          >
            {detectionsActive && params?.row.is_detected && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={"100%"}
              >
                <DetectionsTooltip
                  content={
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.common_black}
                        >
                          {getActiveDetections(params.row?.last_detections)
                            .length +
                            pluralize(
                              " Active event",
                              getActiveDetections(params.row?.last_detections)
                                .length
                            ) +
                            " detected."}
                        </Typography>
                        <Typography variant="caption">
                          Highest Severity:
                        </Typography>
                        <ChipColor
                          value={maxSeverity}
                          type={"controlPriority"}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                      <Typography variant="caption">
                        Click the step to view details
                      </Typography>
                    </Box>
                  }
                >
                  <Box>
                    <DetectionLabelIcon />
                  </Box>
                </DetectionsTooltip>
              </Box>
            )}
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: "mitigating_controls_tools_set",
      headerName: "Tools",
      width: 180,
      align: "left",
      valueFormatter: (params) => {
        if (Array.isArray(params.value)) {
          return params.value.join(" ");
        }
      },
      renderCell: (params) => {
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            {params?.row?.cwe_id ? (
              <CustomToolTip title={"This step is associated with CVEs"}>
                <Box sx={{ display: "flex", justifyItems: "center" }}>
                  <SvgToIcon
                    IconLogo={icons.cveIcon}
                    width={"40px"}
                    style={{ padding: "8px" }}
                  />
                </Box>
              </CustomToolTip>
            ) : params?.row?.protection_level ===
              ProtectionLevelText.MISSING_CONTROLS ? (
              <CustomToolTip title={"This step has no protection in place"}>
                <Box sx={{ display: "flex", justifyItems: "center" }}>
                  <SvgToIcon
                    IconLogo={icons.questionMarkIcon}
                    width={"40px"}
                    style={{ padding: "8px" }}
                  />
                </Box>
              </CustomToolTip>
            ) : (
              params?.value?.map((tool, index) => (
                <IntegrationImage
                  key={`${tool}-${index}`}
                  name={tool}
                  width={"40px"}
                />
              ))
            )}
          </Box>
        );
      },
    },
    ...(filteredScenariosSets ? [impactColumns] : attackPathColumns),

    {
      field: "protection_stats",
      headerName: "Protection",
      width: 120,
      valueFormatter: (params) => {
        return `${params?.value?.compliant}/${params?.value?.total}`;
      },
      renderCell: (params) => {
        if (
          params?.row?.protection_level ===
            ProtectionLevelText.MISSING_CONTROLS ||
          params?.row?.cwe_id
        ) {
          return (
            <Chip
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="overline"
                    sx={{ color: colors.text_secondary }}
                  >
                    -
                  </Typography>
                </Box>
              }
              sx={{
                borderRadius: "12px",
                background: "transparent",
                border: "0.5px solid " + colors.grey_light,
                alignItems: "center",
                display: "flex",
                padding: "5px 0px",
                height: "24px",
              }}
            />
          );
        }

        if (params.value?.total > 0) {
          return (
            <CustomToolTip
              title={protectionToolTipTitle(
                params.value?.compliant,
                params.value?.total
              )}
            >
              <Box>
                <CoverageControlsLabel
                  compliant={params.value?.compliant}
                  total={params.value?.total}
                />
              </Box>
            </CustomToolTip>
          );
        }
        return null;
      },
      sortComparator: (v1, v2, param1, param2) => {
        const value1 = param1.value;
        const value2 = param2.value;

        // If both values are null, consider them equal
        if (value1 === null && value2 === null) return 0;

        // If one of the values is null, ignore it in sorting
        if (value1 === null) return 0;
        if (value2 === null) return 0;

        // Sort primarily by the ratio of compliant to total (coverage)
        const ratio1 = value1.total > 0 ? value1.compliant / value1.total : 0;
        const ratio2 = value2.total > 0 ? value2.compliant / value2.total : 0;

        if (ratio1 !== ratio2) {
          return ratio1 - ratio2;
        }

        // If coverage ratios are equal, sort by total number
        return value1.total - value2.total;
      },
    },
    {
      field: "protection_level",
      headerName: "Protection Level",
      width: 180,
      renderCell: (params) => {
        return <ProtectionLevel level={params.value} />;
      },
      sortComparator: (v1, v2) => {
        const value1 = ProtectionLevelOrder[v1] || 0;
        const value2 = ProtectionLevelOrder[v2] || 0;
        return value1 - value2;
      },
    },
  ];

  const [sortModel, setSortModel] = useState([
    {
      field: "protection_level",
      sort: "asc",
    },
    ...(filteredScenariosSets
      ? [{ field: "impact", sort: "desc" }]
      : [{ field: "number_of_scenarios", sort: "desc" }]),
  ]);

  // Update the sort model when filteredScenariosSets changes
  useEffect(() => {
    setSortModel([
      {
        field: "protection_level",
        sort: "asc",
      },
      // Only include the appropriate sort field based on what column is actually rendered
      ...(filteredScenariosSets
        ? [{ field: "impact", sort: "asc" }]
        : [{ field: "number_of_scenarios", sort: "asc" }]),
    ]);
  }, [filteredScenariosSets]);

  return (
    <StyledDataGrid
      style={{ cursor: "pointer" }}
      columns={columns}
      rows={steps}
      onRowClick={onRowClick}
      onRowSelectionModelChange={onRowSelection}
      rowSelectionModel={selectedRowIDs}
      loading={loading}
      apiRef={refToPass}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      initialState={{
        sorting: {
          sortModel: sortModel,
        },
      }}
      sx={{
        "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
          {
            paddingLeft: "20px",
          },
        ...sx,
      }}
      pageSizeOptionsOverride={rowNumber ? [rowNumber] : [8, 20, 50]}
    />
  );
}
